import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  DocumentImageOptimized,
  documentSrc,
  useDocumentStatus,
} from "../documents/utils";
import ImageViewer from "react-simple-image-viewer";
import { TWclassNames } from "../../utils/Div";
import { PMButton } from "../../utils/PMButton";
import { PencilIcon, TrashIcon, WindowIcon } from "@heroicons/react/24/outline";
import { deleteDocument } from "../../../api/document";
import SimplePrompt from "../../utils/simplePrompt";
import { StringLimit } from "../../Utils";
import { EditDocumentDialog } from "./editDocumentDialog";
import { PMPagination } from "../../utils/PMPagination";
import { ResourceLoading } from "../../utils/ResourceLoading";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";

export const DocumentViewer = (props) => {
  // DocumentViewer will not fetch from API,
  // so the documents object must include nessesary objects.

  const { pathType, perPage, page, onPageChange, totalCount, currentCount } =
    props;
  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [documents, setDocuments] = useState(props.documents || []);

  const { title } = document;

  
  function isPdf(filename) {
    return filename?.toLowerCase().endsWith(".pdf");
  }

  const images = documents?.map((document) =>
    documentSrc(document, "original")
  );

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  const handleDocumentChange = (document) => {
    // find existing document in list
    const index = documents.findIndex((x) => x.id === document.id);

    let prevItems = [...documents];
    prevItems[index] = document;
    setDocuments(prevItems);
  };

  const [selectedRemoveDocument, setSelectedRemoveDocument] = useState(null);

  const [submitRemoveDocumentPromptOpen, setSubmitRemoveDocumentOpen] =
    useState(false);

  const submitRemoveDocument = async (document) => {
    await deleteDocument(document?.id).then((res) => {
      // check if any of these statuses 200, 204 etc.
      if (res.status === 204 || res.status === 200) {
        const newDocuments = documents.filter(
          (item) => item.id !== document.id
        );
        setDocuments(newDocuments);
      }
    });
  };

  const Image = ({ document, onClick, pathType }) => (
    <div
      onClick={onClick}
      className={TWclassNames(
        props.size === "large" ? "" : "max-w-[50px]",
        "ml-2"
      )}
    >
      <DocumentImageOptimized
        pathType={pathType}
        document={document}
        onChange={handleDocumentChange}
      />
    </div>
  );

  useEffect(() => {
    setDocuments(props.documents);
  }, [props.documents]);

  const [selectedEditDocument, setSelectedEditDocument] = useState(null);

  const [editDocumentDialogOpen, setEditDocumentDialogOpen] = useState(false);

  const navigateToPdf = (url) => {
    window.open(url, "_blank");
  };

  const ImageViewerComponent = ({ images, currentImage, closeImageViewer }) => {
    <ImageViewer
      src={images}
      currentIndex={currentImage}
      disableScroll={false}
      closeOnClickOutside={true}
      onClose={closeImageViewer}
      backgroundStyle={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    />;
  };

  function GeneralContent() {
    return (
      <>
        <SimplePrompt
          title="Sikker på at du vill slette dette dokumentet?"
          description="Dokumentet slettes permanent og kan ikke gjenopprettes."
          onAccept={() => submitRemoveDocument(selectedRemoveDocument)}
          open={submitRemoveDocumentPromptOpen}
          onClose={() => setSubmitRemoveDocumentOpen(false)}
        />

        <EditDocumentDialog
          document={selectedEditDocument}
          open={editDocumentDialogOpen}
          onClose={() => setEditDocumentDialogOpen(false)}
          onRefresh={(data) => {
            handleDocumentChange(data);
          }}
        />
      </>
    );
  }

  if (props.size === "single") {
    // Single image view - just return the first document as a simple image
    const document = documents?.[0];

    if (!document) {
      return null;
    }

    // Create a specialized Image component for single view that doesn't trigger onChange
    const SingleImage = ({ document, pathType }) => (
      <div className="w-full h-full overflow-hidden pointer-events-none">
        <DocumentImageOptimized
          pathType={pathType}
          document={document}
          onChange={undefined} // Don't trigger onChange for single view
        />
      </div>
    );

    return (
      <>
        <div className="single-document-viewer">
          {pathType === "quickview" ? (
            <>
              <div className="w-full h-full overflow-hidden">
                <SingleImage pathType={pathType} document={document} />
              </div>
            </>
          ) : (
            <Link to={document?.file?.url}>
              <SingleImage document={document} />
            </Link>
          )}
        </div>
        <GeneralContent />
      </>
    );
  } else if (props.size === "large") {
    return (
      <>
        <div className="grid grid-cols-6 gap-4 ">
          {documents &&
            documents?.map((document, index) => (
              <div
                key={index}
                className="col-span-3 sm:col-span-2 rounded-xl shadow-md px-4 py-4"
              >
                {pathType == "quickview" ? (
                  <>
                    <div className="py-2">
                      <span className="text-xs text-gray-500 flex">
                        <StringLimit
                          text={document?.file?.filename}
                          limit={35}
                        />
                      </span>
                      <div className="text-sm font-semibold flex">
                        <StringLimit text={document?.title} limit={35} />
                        <br />
                      </div>
                    </div>
                    <div className="w-38 h-38 sm:w-48 sm:h-48 overflow-hidden">
                      <Image
                        pathType={pathType}
                        onClick={
                          !isPdf(document?.file?.filename)
                            ? () => openImageViewer(index)
                            : () => navigateToPdf(document?.file?.url)
                        }
                        document={document}
                      />
                    </div>

                    <div className="py-2 flex flex-col sm:flex-row justify-between">
                      <div>
                        <PMButton
                          styleType="minimal"
                          onClick={() => {
                            setSelectedEditDocument(document);
                            setEditDocumentDialogOpen(true);
                          }}
                          // onClick={() => {
                          //   setSelectedRemoveDocument(document);
                          //   setSubmitRemoveDocumentOpen(true);
                          // }}
                          className="py-2"
                        >
                          <PencilIcon className="h-4 w-4 text-red mr-1" />
                          Rediger
                        </PMButton>
                      </div>

                      <div>
                        <PMButton
                          target="_blank"
                          to={document?.file?.url}
                          tooltip="Viser bilde i nytt vindu"
                          styleType="minimal"
                          className="py-2"
                        >
                          <WindowIcon className="h-4 w-4 text-red mr-1" />
                          Nytt vindu
                        </PMButton>
                      </div>

                      <div>
                        <PMButton
                          styleType="minimal"
                          onClick={() => {
                            setSelectedRemoveDocument(document);
                            setSubmitRemoveDocumentOpen(true);
                          }}
                          className=" text-red-400 py-2"
                        >
                          <TrashIcon className="h-4 w-4 text-red mr-1" />
                          Slett bilde
                        </PMButton>
                      </div>
                    </div>

                    {isViewerOpen && (
                      <ImageViewer
                        src={images}
                        currentIndex={currentImage}
                        disableScroll={false}
                        closeOnClickOutside={true}
                        onClose={closeImageViewer}
                        backgroundStyle={{
                          backgroundColor: "rgba(0,0,0,0.9)",
                        }}
                      />
                    )}
                  </>
                ) : (
                  // DocumentImageOptimized already renders its own
                  // <a target="_blank"> for the file URL. Wrapping it in an
                  // extra <Link> created nested anchors that opened two tabs
                  // on a single click.
                  <Image document={document} />
                )}
              </div>
            ))}
        </div>
        <PMPagination
          perPage={perPage}
          onPageChange={onPageChange}
          page={page}
          totalCount={totalCount}
          currentCount={currentCount}
        />
        <GeneralContent />
      </>
    );
  } else {
    return (
      <>
        <div className="relative document-viewer grid grid-cols-1 md:grid-cols-6 lg:grid-cols-10 overflow-auto">
          {documents &&
            documents?.map((document, index) => (
              <div key={index} className="col-span-1">
                {!isPdf(document?.file?.filename) && pathType == "quickview" ? (
                  <>
                    <Image
                      onClick={() => openImageViewer(index)}
                      document={document}
                    />

                    {isViewerOpen && (
                      <div style={{}}>
                        <ImageViewer
                          src={images}
                          currentIndex={currentImage}
                          disableScroll={false}
                          closeOnClickOutside={true}
                          onClose={closeImageViewer}
                          backgroundStyle={{
                            backgroundColor: "rgba(0,0,0,0.9)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  // DocumentImageOptimized already renders its own
                  // <a target="_blank"> for the file URL. Wrapping it in an
                  // extra <Link target="_blank"> created nested anchors that
                  // opened two tabs on a single click.
                  <Image document={document} />
                )}
              </div>
            ))}
        </div>
        <GeneralContent />
      </>
    );
  }
};

DocumentViewer.propTypes = {
  size: PropTypes.oneOf(["base", "small", "large", "single"]),
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      file: PropTypes.shape({
        url: PropTypes.string,
        thumb: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
    })
  ),
  pathType: PropTypes.oneOf(["url", "quickview"]),
};

DocumentViewer.defaultProps = {
  size: "base",
  documents: [],
  pathType: "url",
};
